import { Injectable } from '@angular/core';
import { KeychainAccess, SecureStorage, StorageErrorType } from '@aparajita/capacitor-secure-storage';
import { AUTH_LEGACY_LOCAL_STORAGE_USER_KEY, AUTH_SECURE_STORAGE_USER_KEY } from '../../constants/auth.constants';
import { CpUserResponse } from '../../interfaces/user-response.interface';

const CONVERT_DATE: boolean = false;
const SYNC_WITH_ICLOUD: boolean = false;

function isInvalidDataError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === StorageErrorType.invalidData;
}

@Injectable({
  providedIn: 'root',
})
export class AuthSecureStorageService {
  private pendingWrites: Promise<void> = Promise.resolve();

  async getUserResponse(): Promise<CpUserResponse | null> {
    try {
      return (await SecureStorage.get(
        AUTH_SECURE_STORAGE_USER_KEY,
        CONVERT_DATE,
        SYNC_WITH_ICLOUD,
      )) as CpUserResponse | null;
    } catch (error: unknown) {
      if (isInvalidDataError(error)) {
        await this.clear();
        return null;
      }
      throw error;
    }
  }

  setUserResponse(userResponse: CpUserResponse): Promise<void> {
    return this.afterPendingWrites(() =>
      SecureStorage.set(
        AUTH_SECURE_STORAGE_USER_KEY,
        userResponse,
        CONVERT_DATE,
        SYNC_WITH_ICLOUD,
        KeychainAccess.afterFirstUnlockThisDeviceOnly,
      ),
    );
  }

  clear(): Promise<void> {
    return this.afterPendingWrites(() => this.removeUserResponse());
  }

  removeLegacySession(): void {
    try {
      localStorage.removeItem(AUTH_LEGACY_LOCAL_STORAGE_USER_KEY);
    } catch {
      return;
    }
  }

  private afterPendingWrites(write: () => Promise<void>): Promise<void> {
    const written: Promise<void> = this.pendingWrites.then(write);
    this.pendingWrites = written.catch((): void => undefined);
    return written;
  }

  private async removeUserResponse(): Promise<void> {
    try {
      await SecureStorage.remove(AUTH_SECURE_STORAGE_USER_KEY, SYNC_WITH_ICLOUD);
    } catch {
      await SecureStorage.clear(SYNC_WITH_ICLOUD).catch((): void => undefined);
    }
  }
}
